import React, { Component, createContext } from 'react';
var redirectURL = require('../../redirectURL');

function currentIdentity() {
    return {
        staff_id: localStorage.getItem('staff_id'),
        user_name: localStorage.getItem('username'),
        email: localStorage.getItem('email')
    };
}

// Turns any plan_date shape (a real Date, an ISO datetime string off the API,
// or an already-clean "YYYY-MM-DD" string) into that same canonical
// "YYYY-MM-DD" string - UTC getters, not local, matching how plan_date is
// stored (UTC-midnight for its calendar day - see uploadPlanData). Used
// wherever session.loadDate gets built from a raw plan_data row instead of
// straight from a date-input field's own value - see resumeSessionFromIds
// below, which used to assign firstRow.plan_date directly and stamp every
// scan afterward with an ISO datetime string instead of a plain date,
// invisible to any date-scoped dashboard query expecting the plain form.
function planDateToYMD(value) {
    if (!value) return null;
    var d = new Date(value);
    if (isNaN(d.getTime())) return null;
    var pad = function (n) { return String(n).padStart(2, '0'); };
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
}

function distinct(arr) { return [...new Set(arr)]; }

function formatDate(d) {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day}-${months[parseInt(m, 10) - 1]}-${y}`;
}

function effClass(eff) {
    if (eff >= 90) return 'good';
    if (eff >= 75) return 'ok';
    return 'low';
}

function emptySessionState() {
    return {
        planId: '', loadDate: '', loadType: '', loadVehicle: '', vehicleNumber: '', lsp: '', dockNumber: '', scannerName: '',
        expectedHUs: [],
        // Every HU planned under this plan_id across ALL trip nos, not just this
        // trip's own expectedHUs - see processScanOutcome's wrong-truck check.
        allPlanHUs: [],
        remainingHUs: [],
        loaded: 0,
        scannedValidIds: new Set(),
        duplicateCount: 0,
        wrongTruckCount: 0,
        notFoundCount: 0,
        manualScanCount: 0,
        autoScanCount: 0,
        manualByConsignee: {},
        autoByConsignee: {}
    };
}

export const MobileTruckLoadContext = createContext(null);

// Which language the scan screen's voice-over feedback speaks in - shared here
// (not local Scan state) because the hamburger menu's language dropdown lives
// in MobileHeader, a sibling of the routed screen, not a child of it.
const VOICE_LANG_STORAGE_KEY = 'mobile_scan_voice_lang';

export class MobileTruckLoadProvider extends Component {
    constructor(props) {
        super(props);
        this.state = {
            voiceLang: (typeof localStorage !== 'undefined' && localStorage.getItem(VOICE_LANG_STORAGE_KEY) === 'hi') ? 'hi' : 'en',
            session: emptySessionState(),
            // Vehicles actually completed this browser session (via Finish Loading),
            // keyed by load date - kept separate from the mock generator so a trip the
            // user just walked through shows up on the Dashboard for real, instead of
            // only ever seeing random mock vehicles unrelated to what was just done.
            realTripsByDate: {},
            // Dashboard re-mounts (and re-fetches) every time it's navigated back to,
            // including from a read-only historical Summary view where nothing could
            // have changed - cache its last fetch per date+port so that trip is a
            // cache hit instead of a redundant network round-trip. Cleared for a date
            // by recordCompletedTrip, the one action that can actually change what
            // that date's dashboard should show.
            dashboardCache: {}
        };
    }

    setVoiceLang = (lang) => {
        this.setState({ voiceLang: lang });
        try { localStorage.setItem(VOICE_LANG_STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    };

    // A warehouse switch invalidates any in-progress load - it belonged to the
    // warehouse that was selected when Start Loading was tapped, and Dashboard/Input
    // Plan/Scan all need to reflect the newly-selected warehouse's own data instead.
    componentDidUpdate(prevProps) {
        if (prevProps.portCode !== this.props.portCode) {
            this.resetSessionState();
        }
    }

    // ---------- Session (in-progress load) ----------
    resetSessionState = () => {
        this.setState({ session: emptySessionState() });
        // Explicit fresh start (header menu) or a port switch - either way the
        // operator wants a blank form, not MobileInputPlan re-surfacing its
        // sessionStorage draft of whatever was previously typed. Key literal has
        // to match MobileInputPlan.jsx's DRAFT_STORAGE_KEY.
        try {
            sessionStorage.removeItem('mtl_input_plan_draft');
        } catch (e) { /* ignore */ }
    };

    // expectedHUs is passed in by the caller (real plan data it already fetched via
    // fetchPlanRowsForDate/fetchPlanRowsForTransaction for Start Loading's own
    // validation) rather than recomputed here.
    startSession = (fields) => {
        var hus = fields.expectedHUs || [];
        this.setState({
            session: {
                ...fields,
                expectedHUs: hus,
                remainingHUs: hus.slice(),
                loaded: 0,
                scannedValidIds: new Set(),
                duplicateCount: 0,
                wrongTruckCount: 0,
                notFoundCount: 0,
                manualScanCount: 0,
                autoScanCount: 0,
                manualByConsignee: {},
                autoByConsignee: {}
            }
        });
    };

    // Decides the scan outcome from the actual scanned code and updates session state -
    // triggered by manual entry, a real handheld scanner, or the camera decoder.
    //
    // Match order (duplicate is checked first - once an HU is loaded, any repeat scan
    // of it is a duplicate regardless of which trip it belongs to):
    //   1. Already scanned this session               -> DUPLICATE
    //   2. In the currently selected trip no's rows    -> SUCCESSFUL
    //   3. In this plan id, but under a different trip -> WRONG TRUCK
    //   4. Not in this plan id at all                  -> NOT FOUND
    // #3/#4 are checked against session.allPlanHUs - every real HU planned under
    // this plan_id across ALL trip nos (see MobileInputPlan.startLoading).
    processScanOutcome = (code, isAuto) => {
        var result = null;
        this.setState((prev) => {
            var session = { ...prev.session };
            if (isAuto) { session.autoScanCount += 1; } else { session.manualScanCount += 1; }

            if (session.scannedValidIds.has(code)) {
                session.duplicateCount += 1;
                result = { type: 'duplicate', code };
            } else {
                var remainingIndex = session.remainingHUs.findIndex(hu => hu.id === code);
                if (remainingIndex !== -1) {
                    var newRemaining = session.remainingHUs.slice();
                    var hu = newRemaining.splice(remainingIndex, 1)[0];
                    session.remainingHUs = newRemaining;
                    session.loaded += 1;
                    var newScannedIds = new Set(session.scannedValidIds);
                    newScannedIds.add(hu.id);
                    session.scannedValidIds = newScannedIds;
                    var loadedC = hu.consignee || 'Unknown';
                    if (isAuto) {
                        session.autoByConsignee = { ...session.autoByConsignee, [loadedC]: (session.autoByConsignee[loadedC] || 0) + 1 };
                    } else {
                        session.manualByConsignee = { ...session.manualByConsignee, [loadedC]: (session.manualByConsignee[loadedC] || 0) + 1 };
                    }
                    result = { type: 'valid', code, hu };
                } else {
                    var planRow = (session.allPlanHUs || []).find(r => r.id === code);
                    if (planRow) {
                        session.wrongTruckCount += 1;
                        // hu: planRow (not just tripNo) - same shape the 'valid' case above
                        // returns, so logScan's result.hu.consignee lookup actually has
                        // something to read instead of always landing on null for every
                        // wrong-truck scan.
                        result = { type: 'wrong', code, tripNo: planRow.tripNo, hu: planRow };
                    } else {
                        session.notFoundCount += 1;
                        result = { type: 'notfound', code };
                    }
                }
            }
            return { session };
        });
        return result;
    };

    // Groups a plan+loadtype+tripno's full expected HU list by consignee, and works
    // out how many of each consignee's HUs are still in the (live or historical)
    // remaining list.
    computeConsigneeMetrics = (expectedHUs, remainingHUs, manualByConsignee, autoByConsignee) => {
        const remainingIds = new Set(remainingHUs.map(hu => hu.id));
        const byConsignee = {};
        expectedHUs.forEach(hu => {
            const c = hu.consignee || 'Unknown';
            if (!byConsignee[c]) byConsignee[c] = { consignee: c, expected: 0, remaining: 0 };
            byConsignee[c].expected += 1;
            if (remainingIds.has(hu.id)) byConsignee[c].remaining += 1;
        });
        return Object.values(byConsignee).map(row => ({
            consignee: row.consignee,
            expected: row.expected,
            remaining: row.remaining,
            scanned: row.expected - row.remaining,
            manual: (manualByConsignee && manualByConsignee[row.consignee]) || 0,
            auto: (autoByConsignee && autoByConsignee[row.consignee]) || 0
        }));
    };

    // ---------- Real, upload-driven data (Input Plan / Scan / Summary / Dashboard) ----------

    // Dashboard's numbers for one date - joins lm_truckload_plan_data (expected)
    // against lm_truckload_scan_log (actual), same real-data join Summary already
    // gets via fetchConsigneeSummary, just across every trip planned for the date
    // instead of one. dateLabel is stamped on here (not by the backend) since
    // formatDate is purely a display concern the caller already has.
    // forceRefresh skips the cache entirely - used when the Dashboard screen
    // itself mounts (see MobileDashboard.componentDidMount), so navigating away
    // (e.g. to Input Plan to start/resume a trip) and back always shows what's
    // actually current instead of whatever this date/port last happened to have
    // cached, possibly from before that trip activity even happened.
    fetchDashboardTrips = async (dateStr, forceRefresh) => {
        var cacheKey = dateStr + '::' + (this.props.portCode || '');
        var cached = this.state.dashboardCache[cacheKey];
        if (cached && !forceRefresh) return cached;

        var params = { plan_date: dateStr, loading_port_code: this.props.portCode };
        console.log('[truckload] getDashboardTrips', params);
        var res = await redirectURL.post('/lmconsignees/truckload/getDashboardTrips', params);
        var data = res.data || {};
        var trips = (data.trips || []).map(function (t) { return Object.assign({ dateLabel: formatDate(dateStr) }, t); });
        var result = Object.assign({}, data, { trips: trips });
        this.setState((prev) => ({ dashboardCache: { ...prev.dashboardCache, [cacheKey]: result } }));
        return result;
    };

    // One fetch for the whole day - every row already uploaded for this warehouse
    // scheduled on loadDate (via each row's own plan_date/"Plan Date" field). Input
    // Plan derives its Plan Id options as the distinct transaction_ids in this set,
    // then filters the SAME array client-side once a plan is picked (no second
    // fetch needed) - a row existing here already implies its transaction was
    // truckload_approved at upload time (uploadPlanData rejects otherwise), so no
    // separate approval check is needed here either.
    fetchPlanRowsForDate = async (loadDate) => {
        var params = { loading_port_code: this.props.portCode, plan_date: loadDate };
        console.log('[truckload] getPlanData (by date)', params);
        var res = await redirectURL.post('/lmconsignees/truckload/getPlanData', params);
        return res.data || [];
    };

    fetchTripStatuses = async (transactionId) => {
        var params = { transaction_id: transactionId, loading_port_code: this.props.portCode };
        console.log('[truckload] getTripStatuses', params);
        var res = await redirectURL.post('/lmconsignees/truckload/getTripStatuses', params);
        return res.data || [];
    };

    // Same getPlanData endpoint fetchPlanRowsForDate uses, just keyed by
    // transaction_id instead of loading_port_code+plan_date - every row for this
    // plan across ALL its trips (not just one trip_no), same as Input Plan's own
    // allPlanHUs needs for wrong-truck detection. See resumeSessionFromIds.
    fetchPlanRowsForTransaction = async (transactionId) => {
        var params = { transaction_id: transactionId, loading_port_code: this.props.portCode };
        console.log('[truckload] getPlanData (by transaction)', params);
        var res = await redirectURL.post('/lmconsignees/truckload/getPlanData', params);
        return res.data || [];
    };

    // Idempotent server-side (upsert) - safe to call again if Start Loading is
    // re-tapped after e.g. a network retry.
    // details (vehicle_number/lsp/dock_number/scanner_name/load_type) is stored on
    // the trip_status doc itself (see markTripLoadingStarted) so a trip that's
    // started-but-not-yet-scanned can still be resumed with its real identity
    // prefilled, instead of only working once scan_log has at least one row.
    startTripLoadingReal = async (transactionId, tripNo, details) => {
        var params = Object.assign({ transaction_id: transactionId, trip_no: tripNo, loading_port_code: this.props.portCode }, details, currentIdentity());
        console.log('[truckload] startTripLoading', params);
        return redirectURL.post('/lmconsignees/truckload/startTripLoading', params);
    };

    // Flips loading_completed on the trip_status doc - the one signal Input Plan's
    // Trip No dropdown and the desktop upload slider need to tell "still loading"
    // apart from "fully loaded" (both currently just read as loading_started there).
    finishTripLoadingReal = async (transactionId, tripNo) => {
        var params = Object.assign({ transaction_id: transactionId, trip_no: tripNo, loading_port_code: this.props.portCode }, currentIdentity());
        console.log('[truckload] finishTripLoading', params);
        return redirectURL.post('/lmconsignees/truckload/finishTripLoading', params);
    };

    // The scan UI still updates instantly from its own synchronous local outcome
    // (processScanOutcome) - this call never blocks that: duplicate/wrong/
    // notfound results log this in the background without waiting on it. A
    // 'valid' result is the exception - MobileScan.submitBarcode awaits this
    // one specific call before telling the operator anything, and compares the
    // server's outcome against its own optimistic one, so a scan that raced
    // another scanner for the same HU and lost gets caught BEFORE a false
    // "scanned successfully" ever shows, instead of showing it and then
    // reversing a moment later (see reconcileRaceLoss). MobileScan.jsx itself
    // refuses to call this at all while navigator.onLine is false (see its
    // submitBarcode) - the operator is told up front there's no connection
    // instead of this silently failing.
    // currentIdentity() has to be spread at the TOP level here, not nested under
    // a scanned_by key - the backend's truckloadIdentityFromBody(req) reads
    // req.body.staff_id/user_name/email directly (same as startTripLoadingReal/
    // finishTripLoadingReal below already do it correctly), so nesting it left
    // every recorded scan's stored scanned_by as {staff_id: undefined, username:
    // undefined, email: undefined} - which is exactly why "Scanned By" was
    // showing empty on every export, not just a display-side formatting issue.
    // Returns {ok:false} on failure (network unreachable mid-flight, server
    // error, etc.) rather than throwing - a lost connection here shouldn't
    // crash the scan flow that already completed locally. Deliberately NOT
    // just `return null` (what this used to do) - a caller that only checked
    // "does the response say a different outcome than mine?" treated a failed
    // write identically to a genuine success, since null fails that check too.
    // That's exactly how an operator on a hotspot whose upstream had dropped
    // (wifi still connected, so navigator.onLine stayed true, but nothing
    // could actually reach the API) saw "scanned successfully" for cartons
    // lm_truckload_scan_log never received - {ok:false} makes that failure
    // impossible for a caller to mistake for success. See MobileScan.submitBarcode
    // for how a {ok:false} response now rolls the optimistic local state back
    // (reconcileScanNotSaved) and blocks further scanning instead.
    recordScanReal = async (payload) => {
        var params = Object.assign({}, payload, currentIdentity());
        console.log('[truckload] recordScan', params);
        try {
            var res = await redirectURL.post('/lmconsignees/truckload/recordScan', params);
            return { ok: true, data: res.data || null };
        } catch (e) {
            console.log('recordScan failed', e);
            return { ok: false };
        }
    };

    // Undoes processScanOutcome's 'valid' side effects for one HU and replays
    // them as a 'duplicate' instead - used when the server's claim on this HU
    // (claimPlanDataHuScanned) tells us AFTER THE FACT that another scanner
    // already completed it first, so this device's own optimistic "loaded"
    // credit was wrong. Puts the HU back into remainingHUs (not just decrements
    // loaded) so this device's own loaded + remainingHUs.length count keeps
    // matching expectedHUs.length - from this device's perspective the HU is
    // genuinely unresolved again, even though the trip as a whole isn't stuck
    // (the other scanner's request is what actually finished it). Guarded on
    // scannedValidIds still containing code - if the operator already
    // legitimately re-scanned this same code again before this correction
    // arrived, that later scan's own logic already owns this HU's state and
    // this stale correction must not stomp on it.
    reconcileRaceLoss = (code, hu, isAuto) => {
        this.setState((prev) => {
            var session = { ...prev.session };
            if (!session.scannedValidIds.has(code)) return {};
            var newScannedIds = new Set(session.scannedValidIds);
            newScannedIds.delete(code);
            session.scannedValidIds = newScannedIds;
            session.loaded = Math.max(0, session.loaded - 1);
            session.duplicateCount += 1;
            session.remainingHUs = [hu].concat(session.remainingHUs);
            var lostC = hu.consignee || 'Unknown';
            if (isAuto) {
                session.autoByConsignee = { ...session.autoByConsignee, [lostC]: Math.max(0, (session.autoByConsignee[lostC] || 0) - 1) };
            } else {
                session.manualByConsignee = { ...session.manualByConsignee, [lostC]: Math.max(0, (session.manualByConsignee[lostC] || 0) - 1) };
            }
            return { session };
        });
    };

    // Undoes processScanOutcome's 'valid' side effects for one HU when
    // recordScanReal comes back {ok:false} - the write never reached the
    // server at all (network unreachable, timeout, server error), as opposed
    // to reconcileRaceLoss above where the server WAS reached and explicitly
    // said another device won. Deliberately does NOT bump duplicateCount like
    // reconcileRaceLoss does - this HU was never actually a duplicate, it just
    // never got recorded, so it goes back to being plain unresolved rather
    // than being attributed to an exception category it doesn't belong to.
    reconcileScanNotSaved = (code, hu, isAuto) => {
        this.setState((prev) => {
            var session = { ...prev.session };
            if (!session.scannedValidIds.has(code)) return {};
            var newScannedIds = new Set(session.scannedValidIds);
            newScannedIds.delete(code);
            session.scannedValidIds = newScannedIds;
            session.loaded = Math.max(0, session.loaded - 1);
            session.remainingHUs = [hu].concat(session.remainingHUs);
            var c = hu.consignee || 'Unknown';
            if (isAuto) {
                session.autoByConsignee = { ...session.autoByConsignee, [c]: Math.max(0, (session.autoByConsignee[c] || 0) - 1) };
            } else {
                session.manualByConsignee = { ...session.manualByConsignee, [c]: Math.max(0, (session.manualByConsignee[c] || 0) - 1) };
            }
            return { session };
        });
    };

    fetchConsigneeSummary = async (transactionId, tripNo) => {
        var params = { transaction_id: transactionId, trip_no: tripNo, loading_port_code: this.props.portCode };
        console.log('[truckload] getConsigneeSummary', params);
        var res = await redirectURL.post('/lmconsignees/truckload/getConsigneeSummary', params);
        return res.data;
    };

    fetchScanLogReal = async (transactionId, tripNo) => {
        var params = { transaction_id: transactionId, trip_no: tripNo, loading_port_code: this.props.portCode };
        console.log('[truckload] getScanLog', params);
        var res = await redirectURL.post('/lmconsignees/truckload/getScanLog', params);
        return res.data || [];
    };

    // Rebuilds session progress from the trip's real scan_log instead of starting
    // over with every HU marked remaining - for picking a trip that's
    // loading_started but not loading_completed with no live local session (e.g.
    // the app was closed mid-scan and the in-memory session was lost with it).
    // Every scan was already persisted via recordScanReal as it happened, so this
    // reconstructs exactly what a still-live session would show.
    resumeSession = (fields, scanRows) => {
        var hus = fields.expectedHUs || [];
        var remaining = hus.slice();
        var scannedValidIds = new Set();
        var duplicateCount = 0, wrongTruckCount = 0, notFoundCount = 0, manualScanCount = 0, autoScanCount = 0;
        var manualByConsignee = {}, autoByConsignee = {};
        var loaded = 0;
        (scanRows || []).forEach((r) => {
            if (r.is_auto) { autoScanCount += 1; } else { manualScanCount += 1; }
            if (r.outcome === 'duplicate') { duplicateCount += 1; }
            else if (r.outcome === 'wrong') { wrongTruckCount += 1; }
            else if (r.outcome === 'notfound') { notFoundCount += 1; }
            else if (r.outcome === 'valid' && !scannedValidIds.has(r.hu)) {
                var idx = remaining.findIndex(hu => hu.id === r.hu);
                // A historical valid scan whose HU isn't in the CURRENT expected
                // set (idx === -1) means the plan was edited/reduced after this
                // scan was recorded - counting it toward `loaded` would inflate
                // progress past `expected.length` (the exact "295 of 277, 106%"
                // bug) instead of just not representing any of today's still-
                // remaining cartons. Same "drop orphan rows, don't fabricate
                // entries for them" fix as getDashboardTrips.
                if (idx === -1) return;
                scannedValidIds.add(r.hu);
                loaded += 1;
                var consignee = remaining[idx].consignee || 'Unknown';
                remaining.splice(idx, 1);
                if (r.is_auto) { autoByConsignee[consignee] = (autoByConsignee[consignee] || 0) + 1; }
                else { manualByConsignee[consignee] = (manualByConsignee[consignee] || 0) + 1; }
            }
        });
        this.setState({
            session: {
                ...fields,
                expectedHUs: hus,
                remainingHUs: remaining,
                loaded,
                scannedValidIds,
                duplicateCount, wrongTruckCount, notFoundCount, manualScanCount, autoScanCount,
                manualByConsignee, autoByConsignee
            }
        });
    };

    // Rebuilds a live session (same as resumeSession above) AND returns a
    // Summary-ready "opts" object (same shape MobileScan.viewSummary already
    // builds by hand) purely from transaction_id/trip_no - the two pieces of
    // state that actually survive a real page refresh, because they're now
    // carried in the URL itself (see the /lm-mobile/scan and /lm-mobile/summary
    // route params), unlike ctx.session (wiped) or router location.state
    // (browser-dependent whether it survives a reload at all). Used by both
    // Scan and Summary's componentDidMount when they're mounted with nothing
    // live to work from. Returns null if this transaction_id/trip_no combo
    // turns out to have no plan rows at all (nothing real to resume).
    resumeSessionFromIds = async (transactionId, tripNo) => {
        var [planRows, statusRows] = await Promise.all([
            this.fetchPlanRowsForTransaction(transactionId),
            this.fetchTripStatuses(transactionId)
        ]);
        var statusRow = statusRows.find((r) => String(r.trip_no) === String(tripNo));
        var allPlanHUs = planRows.map((r) => ({ id: r.hu, sub: r.doc_no, consignee: r.consignee, docType: r.doc_type, tripNo: r.trip_no }));
        var hus = allPlanHUs.filter((r) => r.tripNo === tripNo);
        if (hus.length === 0) return null;
        var firstRow = planRows.find((r) => r.trip_no === tripNo) || {};
        var sessionFields = {
            loadDate: planDateToYMD(firstRow.plan_date), planId: transactionId, loadType: firstRow.trip_type, loadVehicle: tripNo,
            vehicleNumber: (statusRow && statusRow.vehicle_number) || '', lsp: (statusRow && statusRow.lsp) || '',
            dockNumber: (statusRow && statusRow.dock_number) || '', scannerName: (statusRow && statusRow.scanner_name) || '',
            expectedHUs: hus, allPlanHUs: allPlanHUs
        };
        var scanRows = await this.fetchScanLogReal(transactionId, tripNo);
        this.resumeSession(sessionFields, scanRows);

        // Best-effort - a failed aggregation shouldn't block getting back to a
        // resumable screen, same fallback-to-local-counts approach viewSummary
        // already uses when this same fetch fails.
        var real = await this.fetchConsigneeSummary(transactionId, tripNo).catch(() => null);
        var expected = real ? real.expected : hus.length;
        var loaded = real ? real.loaded : 0;
        var duplicate = real ? real.duplicateCount : 0;
        var wrongTruck = real ? real.wrongTruckCount : 0;
        var notFound = real ? real.notFoundCount : 0;
        var completed = !!(statusRow && statusRow.loading_completed);
        // A refresh landing on an already-finished trip should come back showing
        // the historical (Download Report) view, not the live one (Finish
        // Loading/Export Data on a trip that's already done) - same distinction
        // getDashboardTrips already draws for the Dashboard's own cards.
        if (completed) {
            var totalScans = loaded + duplicate + wrongTruck + notFound;
            return {
                mode: 'historical',
                breadcrumbLabel: 'Summary',
                trip: {
                    planId: sessionFields.planId, tripNo: tripNo, dateLabel: formatDate(sessionFields.loadDate),
                    vehicle: sessionFields.vehicleNumber || tripNo, lsp: sessionFields.lsp, dockNumber: sessionFields.dockNumber,
                    expected: expected, actualLoaded: loaded, duplicate: duplicate, wrongTruck: wrongTruck, notFound: notFound,
                    autoScans: real ? real.autoScanCount : 0, manualScans: real ? real.manualScanCount : 0,
                    completed: true,
                    efficiency: totalScans > 0 ? Math.round((loaded / totalScans) * 100) : 0,
                    progress: expected > 0 ? Math.round((loaded / expected) * 100) : 0
                },
                vehicle: sessionFields.vehicleNumber || tripNo,
                date: formatDate(sessionFields.loadDate),
                planId: sessionFields.planId,
                loadType: sessionFields.loadType,
                tripNo: tripNo,
                lsp: sessionFields.lsp,
                dockNumber: sessionFields.dockNumber,
                scannerName: sessionFields.scannerName,
                expectedTotal: expected,
                scannedTotal: loaded,
                remainingTotal: real ? real.remaining : 0,
                autoScans: real ? real.autoScanCount : 0,
                manualScans: real ? real.manualScanCount : 0,
                duplicateTotal: duplicate,
                wrongTruckTotal: wrongTruck,
                notFoundTotal: notFound,
                consigneeRows: real ? real.consignees : this.computeConsigneeMetrics(hus, [], {}, {}),
                returnScreen: 'dashboard'
            };
        }
        return {
            mode: 'live',
            breadcrumbLabel: 'Summary',
            vehicle: sessionFields.vehicleNumber || tripNo,
            date: formatDate(sessionFields.loadDate),
            planId: sessionFields.planId,
            loadType: sessionFields.loadType,
            tripNo: tripNo,
            lsp: sessionFields.lsp,
            dockNumber: sessionFields.dockNumber,
            scannerName: sessionFields.scannerName,
            expectedTotal: expected,
            scannedTotal: loaded,
            remainingTotal: real ? real.remaining : hus.length,
            autoScans: real ? real.autoScanCount : 0,
            manualScans: real ? real.manualScanCount : 0,
            duplicateTotal: duplicate,
            wrongTruckTotal: wrongTruck,
            notFoundTotal: notFound,
            consigneeRows: real ? real.consignees : this.computeConsigneeMetrics(hus, hus, {}, {}),
            returnScreen: 'scan'
        };
    };

    recordCompletedTrip = (dateStr, trip) => {
        this.setState((prev) => {
            var existing = prev.realTripsByDate[dateStr] || [];
            // Re-finishing the same vehicle on the same date replaces its earlier
            // entry instead of listing it twice.
            var updated = [trip, ...existing.filter(t => t.vehicle !== trip.vehicle)];
            // This date's dashboard cache (see fetchDashboardTrips) is now stale for
            // every port it was cached under - drop all of them so the next visit
            // fetches the real, just-updated numbers instead of the pre-finish snapshot.
            var newCache = {};
            Object.keys(prev.dashboardCache).forEach((key) => {
                if (!key.startsWith(dateStr + '::')) newCache[key] = prev.dashboardCache[key];
            });
            return {
                realTripsByDate: { ...prev.realTripsByDate, [dateStr]: updated },
                dashboardCache: newCache
            };
        });
    };

    render() {
        var portCode = this.props.portCode;
        var value = {
            portCode: portCode,
            voiceLang: this.state.voiceLang,
            setVoiceLang: this.setVoiceLang,
            session: this.state.session,
            realTripsByDate: this.state.realTripsByDate,
            resetSessionState: this.resetSessionState,
            startSession: this.startSession,
            processScanOutcome: this.processScanOutcome,
            computeConsigneeMetrics: this.computeConsigneeMetrics,
            recordCompletedTrip: this.recordCompletedTrip,
            fetchPlanRowsForDate: this.fetchPlanRowsForDate,
            fetchPlanRowsForTransaction: this.fetchPlanRowsForTransaction,
            fetchTripStatuses: this.fetchTripStatuses,
            startTripLoadingReal: this.startTripLoadingReal,
            finishTripLoadingReal: this.finishTripLoadingReal,
            recordScanReal: this.recordScanReal,
            reconcileRaceLoss: this.reconcileRaceLoss,
            reconcileScanNotSaved: this.reconcileScanNotSaved,
            fetchConsigneeSummary: this.fetchConsigneeSummary,
            fetchScanLogReal: this.fetchScanLogReal,
            resumeSession: this.resumeSession,
            resumeSessionFromIds: this.resumeSessionFromIds,
            fetchDashboardTrips: this.fetchDashboardTrips,
            formatDate,
            effClass,
            distinct
        };
        return (
            <MobileTruckLoadContext.Provider value={value}>
                {this.props.children}
            </MobileTruckLoadContext.Provider>
        );
    }
}
